import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAccessToken, getCurrentUserOrLogoutRedirect } from '../../auth';
import Button from '../../lib/button/Button';
import { formatCurrency } from '../../localization';
import Chip from '../../lib/chip/Chip';
import axios from 'axios';
import { capitalizeFirstLetter } from '@elyx-code/project-logic-tree';
import { envConfig } from '../../config';
import { useWorkspaceStore } from '../../store/workspace';
import { EditorService } from '../../services/editor';

interface ITransactionHistoryDialogProps {
	onClose: () => void;
	project: EditorService;
}

const TransactionHistoryDialog: React.FC<ITransactionHistoryDialogProps> = ({
	onClose,
	project
}) => {
	const [transactions, setTransactions] = useState<any[]>([]);
	const [user, setUser] = useState<any | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { id: projectId } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);

	const getTransactions = async (
		{ page }: { page?: number } = { page: 1 }
	) => {
		if (!projectId) return;

		const previouslyFetchedTransactions = transactions;

		const response = await axios.get(
			`${envConfig.API_BASE_URL}/pricing-service/v1/transactions/${activeWorkspace.id}?projectId=${projectId}&limit=10${
				previouslyFetchedTransactions.length
					? '&lastTransactionId=' +
						previouslyFetchedTransactions[
							previouslyFetchedTransactions.length - 1
						].id
					: ''
			}`,
			{
				headers: {
					Authorization: 'Bearer ' + (await getAccessToken())
				}
			}
		);

		return response.data.items;
	};

	const init = async () => {
		setIsLoading(true);
		const [fetchedTransactions, fetchedUser] = await Promise.all([
			getTransactions(),
			getCurrentUserOrLogoutRedirect()
		]);

		setTransactions(fetchedTransactions || []);
		setUser(fetchedUser);
		setIsLoading(false);
	};

	useEffect(() => {
		init();
	}, []);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				padding: '40px'
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
				<button className="dialog_back_button" onClick={onClose}>
					<i className="fa-solid fa-arrow-left" />
				</button>
				<h1
					style={{ margin: '0px', fontWeight: 500, fontSize: '28px' }}
				>
					Transaction history
				</h1>
			</div>
			<a
				style={{
					display: 'flex',
					gap: '6px',
					marginLeft: '42px',
					textDecoration: 'none',
					alignItems: 'center'
				}}
				href=""
				onClick={(event) => {
					event.preventDefault();
					navigate(
						`${project?.editorBasePath}/costs/billing-history`
					);
				}}
			>
				<i
					className="fa-solid fa-money-check-dollar"
					style={{ color: 'rgba(0,0,0,0.7)' }}
				/>
				<p style={{ margin: '0px', color: 'rgba(0,0,0,0.7)' }}>
					Payment methods
				</p>
			</a>
			<div style={{ flex: 1, margin: '0 42px', marginTop: '30px' }}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						minHeight: '80%',
						height: '100%'
					}}
				>
					<span
						style={{
							display: 'flex',
							gap: '10px',
							alignItems: 'center'
						}}
					>
						<h3
							style={{
								display: 'flex',
								gap: '6px',
								fontWeight: 'normal',
								margin: '0px'
							}}
						>
							Billing history
						</h3>
					</span>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '10px'
						}}
					>
						<p
							style={{
								margin: '0px',
								fontSize: '14px',
								marginTop: '6px',
								color: 'rgba(0,0,0,0.54)'
							}}
						>
							{`List of past bills and transactions from the costs incured by this project.`}
							<br />
							<br />
							{`Bills are sent on the first day of each month by email to: `}
							<b>{user?.email}</b>
						</p>
					</div>
					<ul
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							marginTop: '20px',
							paddingLeft: '0px'
						}}
					>
						{isLoading && (
							<div style={{ marginTop: '20px', color: 'rgba(0,0,0,0.54)', fontSize: '14px' }}>
								Loading transactions...
							</div>
						)}
						{!isLoading && transactions.length === 0 && (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									padding: '48px 20px',
									gap: '16px',
									backgroundColor: 'rgba(0,0,0,0.02)',
									borderRadius: '12px',
									border: '1px dashed rgba(0,0,0,0.1)',
									marginTop: '20px'
								}}
							>
								<div 
									style={{ 
										width: '48px', 
										height: '48px', 
										borderRadius: '50%', 
										backgroundColor: 'rgba(0,0,0,0.04)', 
										display: 'flex', 
										alignItems: 'center', 
										justifyContent: 'center' 
									}}
								>
									<i
										className="fa-solid fa-receipt"
										style={{
											fontSize: '20px',
											color: 'rgba(0,0,0,0.3)'
										}}
									/>
								</div>
								<div style={{ textAlign: 'center' }}>
									<h4
										style={{
											margin: '0 0 8px 0',
											color: 'rgba(0,0,0,0.7)',
											fontWeight: 500,
											fontSize: '16px'
										}}
									>
										No transactions yet
									</h4>
									<p
										style={{
											margin: '0px',
											fontSize: '14px',
											color: 'rgba(0,0,0,0.54)',
											maxWidth: '250px'
										}}
									>
										When you incur costs, your past bills and payments will appear here.
									</p>
								</div>
							</div>
						)}
						{!isLoading && transactions.length > 0 && transactions.map((transaction) => (
							<li
								style={{
									display: 'flex',
									gap: '10px',
									alignItems: 'start'
								}}
								key={transaction.id}
							>
								<h4 style={{ margin: '0px', fontSize: '18px' }}>
									{formatCurrency(
										transaction.amount / 100,
										transaction.currency
									)}
								</h4>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '4px'
									}}
								>
									<div
										style={{
											display: 'flex',
											gap: '8px',
											alignItems: 'center'
										}}
									>
										<i
											className="fa-regular fa-credit-card"
											style={{
												color: 'rgba(0,0,0,0.54)',
												fontSize: '14px'
											}}
										/>
										<p
											style={{
												fontSize: '14px',
												color: 'rgba(0,0,0,0.54)',
												margin: '0px'
											}}
										>
											{capitalizeFirstLetter(
												transaction
													.payment_method_details.card
													.brand
											) + ' ending in '}
										</p>
										<p
											style={{
												fontSize: '14px',
												color: 'rgba(0,0,0,0.54)',
												margin: '0px'
											}}
										>
											{'****' +
												transaction
													.payment_method_details.card
													.last4}
										</p>
										<Chip color="success">
											{transaction.status}
										</Chip>
										<a
											href={transaction.receipt_url}
											target="_blank"
											style={{
												textDecoration: 'none',
												color: 'var(--color-main-grey)'
											}}
										>
											<i className="fa-solid fa-receipt" />
										</a>
									</div>
									<div
										style={{
											display: 'flex',
											gap: '8px',
											justifyContent: 'flex-start',
											alignItems: 'center'
										}}
									>
										<p
											style={{
												fontSize: '14px',
												color: 'rgba(0,0,0,0.54)',
												margin: '0px'
											}}
										>
											{new Date(
												transaction.created * 1000
											).toLocaleString()}
										</p>
									</div>
								</div>
							</li>
						))}
					</ul>
				</div>
			</div>
			<div
				style={{
					alignSelf: 'flex-start',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-end',
					width: '100%',
					gap: '16px'
				}}
			>
				<Button onClick={() => navigate(`${project?.editorBasePath}`)}>
					Back
				</Button>
			</div>
		</div>
	);
};

export default TransactionHistoryDialog;
